import React, { useState } from 'react';
import { FEATURE_FLAGS } from '../config/featureFlags';

const RoomList = ({ rooms, onReserve, isMobileDialog = false }) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(null);

  // Helper function to get full image URL
  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http')) return photoUrl;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL.replace('/api', '');
    return `${API_BASE_URL}${photoUrl}`;
  };

  const openCarousel = (images, index = 0) => {
    setCarouselImages(images);
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const closeCarousel = () => setCarouselOpen(false);

  const openDetailsModal = (roomType) => {
    setSelectedRoomType(roomType);
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedRoomType(null);
  };

  const nextImage = () =>
    setCarouselIndex((prev) => (prev + 1) % carouselImages.length);

  const prevImage = () =>
    setCarouselIndex((prev) =>
      prev === 0 ? carouselImages.length - 1 : prev - 1
    );

    
    
  return (
    <>
      <div className="room-list">
        {rooms.map(roomType => {
          // Debug logging for tent rooms
          if (roomType.name && roomType.name.toLowerCase().includes('tent')) {
            console.log('Tent room data:', {
              name: roomType.name,
              available_rooms: roomType.available_rooms,
              is_day_restricted: roomType.is_day_restricted
            });
          }
          
          return (
          <div 
            key={roomType.id} 
            className={isMobileDialog ? "room-card room-card-mobile" : "room-card room-card-flex"}
            style={isMobileDialog ? {
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '20px 16px',
              margin: '12px 0'
            } : {}}
          >
            {isMobileDialog ? (
              // Mobile Dialog Layout - Vertical Stack
              <>
                {/* Header with name and price */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 
                      onClick={() => openDetailsModal(roomType)}
                      style={{ 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        color: '#4f8cff',
                        margin: '0 0 8px 0',
                        fontSize: '1.2em'
                      }}
                      title="Click for more details"
                    >
                      {roomType.name}
                    </h3>
                    <p style={{ margin: '0 0 4px 0', color: '#2a3b5c', fontSize: '0.95em' }}>
                      Capacity: {roomType.max_capacity} adults
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '1.1em', 
                      fontWeight: 'bold', 
                      color: '#4f8cff',
                      marginBottom: '4px'
                    }}>
                      ₱{roomType.price_per_night.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      per night
                    </div>
                  </div>
                </div>

                {/* Photos in horizontal scroll */}
                {roomType.photos && roomType.photos.length > 0 && (
                  <div className="room-photos-scroll" style={{ marginBottom: '12px' }}>
                    {roomType.photos.slice(0, 6).map((photoUrl, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(photoUrl)}
                        alt={`${roomType.name} thumbnail ${idx + 1}`}
                        style={{
                          width: '80px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: '1px solid #e3f0ff',
                          flexShrink: 0
                        }}
                        onClick={() => openCarousel(roomType.photos.map(getImageUrl), idx)}
                      />
                    ))}
                  </div>
                )}

                {/* Availability and Reserve button */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '12px',
                  borderTop: '1px solid #e3f0ff'
                }}>
                  <div>
                    <span style={{ 
                      color: roomType.available_rooms <= 0 ? '#ef4444' : roomType.available_rooms <= 3 ? '#ef4444' : '#22c55e', 
                      fontSize: '0.9em', 
                      fontWeight: 600 
                    }}>
                      {roomType.available_rooms > 0 ? `${roomType.available_rooms} rooms left!` : 'No rooms available'}
                    </span>
                  </div>
                  {roomType.available_rooms > 0 ? (
                    <button 
                      onClick={() => onReserve(roomType)}
                      style={{
                        background: 'linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      Reserve this room!
                    </button>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '4px'
                    }}>
                      <div style={{
                        background: roomType.is_day_restricted ? '#6b7280' : '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        fontWeight: '700',
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                        width: 'fit-content',
                        maxWidth: '100%'
                      }}>
                        {roomType.is_day_restricted ? 'UNAVAILABLE' : 'SOLD OUT'}
                      </div>
                      {FEATURE_FLAGS.TENT_WEEKEND_ONLY_RESTRICTION && roomType.is_day_restricted && (
                        <div style={{
                          fontSize: '0.7em',
                          color: '#6b7280',
                          fontStyle: 'italic',
                          textAlign: 'right'
                        }}>
                          Available only on Fridays to Sundays
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Desktop Layout - Horizontal Flex (original)
              <>
                {/* Left: Info */}
                <div className="room-info">
                  <h3 
                    onClick={() => openDetailsModal(roomType)}
                    style={{ 
                      cursor: 'pointer', 
                      textDecoration: 'underline',
                      color: '#4f8cff'
                    }}
                    title="Click for more details"
                  >
                    {roomType.name}
                  </h3>
                  <p>Capacity: {roomType.max_capacity} adults</p>
                  <p>
                    ₱{roomType.price_per_night.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} per night
                  </p>
                </div>
                {/* Center: Thumbnails */}
                <div className="room-thumbnails center-thumbnails">
                  {roomType.photos && roomType.photos.length > 0 ? (
                    roomType.photos.slice(0, 4).map((photoUrl, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(photoUrl)}
                        alt={`${roomType.name} thumbnail ${idx + 1}`}
                        className="room-thumbnail"
                        onClick={() => openCarousel(roomType.photos.map(getImageUrl), idx)}
                      />
                    ))
                  ) : (
                    <div className="no-photos-placeholder">
                      <span>No photos available</span>
                    </div>
                  )}
                </div>
                {/* Right: Reserve & Availability */}
                <div className="room-action">
                  <div className="room-left-indicator">
                    <span style={{ 
                      color: roomType.available_rooms <= 0 ? '#ef4444' : roomType.available_rooms <= 3 ? 'red' : '#22c55e', 
                      fontSize: '0.95em', 
                      fontWeight: 500 
                    }}>
                      {roomType.available_rooms > 0 ? `${roomType.available_rooms} rooms left!` : 'No rooms available'}
                    </span>
                  </div>
                  {roomType.available_rooms > 0 ? (
                    <button onClick={() => onReserve(roomType)}>Reserve this room!</button>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '4px'
                    }}>
                      <div style={{
                        background: roomType.is_day_restricted ? '#6b7280' : '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px 24px',
                        fontWeight: '700',
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                        width: 'fit-content',
                        maxWidth: '100%',
                        textAlign: 'center'
                      }}>
                        {roomType.is_day_restricted ? 'UNAVAILABLE' : 'SOLD OUT'}
                      </div>
                      {FEATURE_FLAGS.TENT_WEEKEND_ONLY_RESTRICTION && roomType.is_day_restricted && (
                        <div style={{
                          fontSize: '0.7em',
                          color: '#6b7280',
                          fontStyle: 'italic'
                        }}>
                          Available only on Fridays to Sundays
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          );
        })}
      </div>

      {carouselOpen && (
        <div
          className="carousel-overlay"
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onClick={closeCarousel}
        >
          <div
            className="carousel-content"
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={carouselImages[carouselIndex]}
              alt="Room"
              style={{
                maxWidth: "70vw",
                maxHeight: "60vh",
                borderRadius: "10px",
                marginBottom: "16px"
              }}
            />
            <div style={{ display: "flex", gap: window.innerWidth <= 768 ? "12px" : "16px", justifyContent: "center", alignItems: "center" }}>
              <button 
                onClick={prevImage}
                style={window.innerWidth <= 768 ? {
                  padding: "12px 16px",
                  fontSize: "18px",
                  minWidth: "50px",
                  borderRadius: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  cursor: "pointer"
                } : {}}
              >
                {window.innerWidth <= 768 ? "‹" : "< Prev"}
              </button>
              <button 
                onClick={nextImage}
                style={window.innerWidth <= 768 ? {
                  padding: "12px 16px",
                  fontSize: "18px",
                  minWidth: "50px",
                  borderRadius: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  cursor: "pointer"
                } : {}}
              >
                {window.innerWidth <= 768 ? "›" : "Next >"}
              </button>
              <button 
                onClick={closeCarousel}
                style={window.innerWidth <= 768 ? {
                  padding: "12px 16px",
                  fontSize: "14px",
                  borderRadius: "8px",
                  background: "#4f8cff",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer"
                } : {}}
              >
                Close
              </button>
            </div>
            <div style={{ marginTop: "8px", color: "#888" }}>
              {carouselIndex + 1} / {carouselImages.length}
            </div>
          </div>
        </div>
      )}

      {detailsModalOpen && selectedRoomType && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500
          }}
          onClick={closeDetailsModal}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "90vw",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 8px 32px rgba(44, 62, 80, 0.15)"
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: "20px" }}>
              <h2 style={{ 
                margin: "0 0 16px 0", 
                color: "#4f8cff",
                borderBottom: "2px solid #e3f0ff",
                paddingBottom: "8px"
              }}>
                {selectedRoomType.name}
              </h2>
              
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "1fr 1fr", 
                gap: "16px",
                marginBottom: "20px",
                padding: "16px",
                background: "#f7fbff",
                borderRadius: "8px",
                border: "1.5px solid #e3f0ff"
              }}>
                <div>
                  <strong style={{ color: "#2a3b5c" }}>Capacity:</strong>
                  <p style={{ margin: "4px 0", fontSize: "1.1em" }}>
                    {selectedRoomType.max_capacity} adults
                  </p>
                </div>
                <div>
                  <strong style={{ color: "#2a3b5c" }}>Price:</strong>
                  <p style={{ margin: "4px 0", fontSize: "1.1em", color: "#4f8cff", fontWeight: "600" }}>
                    ₱{selectedRoomType.price_per_night.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} per night
                  </p>
                </div>
                <div>
                  <strong style={{ color: "#2a3b5c" }}>Available:</strong>
                  <p style={{ margin: "4px 0", fontSize: "1.1em", color: selectedRoomType.available_rooms > 0 ? "#22c55e" : "#ef4444" }}>
                    {selectedRoomType.available_rooms} rooms
                  </p>
                </div>
                
              </div>

              {selectedRoomType.description && (
                <div style={{ marginBottom: "20px" }}>
                  <h3 style={{ color: "#2a3b5c", marginBottom: "8px" }}>Description</h3>
                  <div style={{ 
                    lineHeight: "1.6", 
                    color: "#2a3b5c",
                    padding: "12px",
                    background: "#f9fafb",
                    borderLeft: "4px solid #4f8cff",
                    borderRadius: "4px",
                    whiteSpace: "pre-wrap"
                  }}>
                    {selectedRoomType.description}
                  </div>
                </div>
              )}

              {selectedRoomType.photos && selectedRoomType.photos.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <h3 style={{ color: "#2a3b5c", marginBottom: "12px" }}>Photos</h3>
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", 
                    gap: "8px" 
                  }}>
                    {selectedRoomType.photos.slice(0, 6).map((photoUrl, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(photoUrl)}
                        alt={`${selectedRoomType.name} photo ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          cursor: "pointer",
                          border: "1px solid #e3f0ff"
                        }}
                        onClick={() => {
                          closeDetailsModal();
                          openCarousel(selectedRoomType.photos.map(getImageUrl), idx);
                        }}
                      />
                    ))}
                  </div>
                  {selectedRoomType.photos.length > 6 && (
                    <p style={{ fontSize: "0.9em", color: "#666", marginTop: "8px" }}>
                      +{selectedRoomType.photos.length - 6} more photos (click any photo to view gallery)
                    </p>
                  )}
                </div>
              )}
            </div>

            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              paddingTop: "16px",
              borderTop: "1px solid #e3f0ff"
            }}>
              <button 
                onClick={closeDetailsModal}
                style={{
                  background: "#6b7280",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
              {selectedRoomType.available_rooms > 0 ? (
                <button 
                  onClick={() => {
                    closeDetailsModal();
                    onReserve(selectedRoomType);
                  }}
                  style={{
                    background: "linear-gradient(90deg, #4f8cff 0%, #6ec6ff 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600"
                  }}
                >
                  Reserve this room!
                </button>
              ) : (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "4px"
                }}>
                  <div style={{
                    background: selectedRoomType.is_day_restricted ? "#6b7280" : "#ef4444",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "12px 24px",
                    fontWeight: "700",
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                    width: "fit-content",
                    maxWidth: "100%"
                  }}>
                    {selectedRoomType.is_day_restricted ? 'UNAVAILABLE' : 'SOLD OUT'}
                  </div>
                  {FEATURE_FLAGS.TENT_WEEKEND_ONLY_RESTRICTION && selectedRoomType.is_day_restricted && (
                    <div style={{
                      fontSize: "0.7em",
                      color: "#6b7280",
                      fontStyle: "italic"
                    }}>
                      Available only on Fridays to Sundays
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomList;